import { useState, useMemo, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import { useAnalytics } from '../context/AnalyticsContext'
import { supabase } from '../lib/supabase'
import Modal from '../components/Modal'
import Loader from '../components/Loader'
import LegalDocContent from '../components/LegalDocContent'
import './Paywall.css'

const TERMS_PDF_URL = '/legal/terms-and-conditions.pdf'

const NETLIFY_FUNCTIONS = import.meta.env.VITE_NETLIFY_FUNCTIONS_URL || ''

const BENEFIT_KEYS_MALE = [
  { key: 'message', icon: '💬', titleKey: 'paywall.benefitMessageTitle', descKey: 'paywall.benefitMessageDesc' },
  { key: 'likes', icon: '❤️', titleKey: 'paywall.benefitLikesTitle', descKey: 'paywall.benefitLikesDesc' },
  { key: 'super_likes', icon: '🌟', titleKey: 'paywall.benefitSuperLikesTitle', descKey: 'paywall.benefitSuperLikesDesc' },
  { key: 'see_likes', icon: '👀', titleKey: 'paywall.benefitSeeLikesTitle', descKey: 'paywall.benefitSeeLikesDesc' },
  { key: 'compatibility', icon: '✨', titleKey: 'paywall.benefitCompatibilityTitle', descKey: 'paywall.benefitCompatibilityDesc' },
  { key: 'photos', icon: '📸', titleKey: 'paywall.benefitPhotosTitle', descKey: 'paywall.benefitPhotosDesc' }
]

const REASON_TO_BENEFIT_KEY = {
  chat_no_match: 'message',
  like_limit_reached: 'likes',
  super_like_no_access: 'super_likes',
  super_like_limit_reached: 'super_likes',
  likes_you: 'see_likes',
  compatibility_details: 'compatibility',
  photo_in_chat: 'photos',
  view_photo_in_chat: 'photos'
}

// Отдельный ивент на каждый возможный reason пейвола
const REASON_TO_PAYWALL_EVENT = {
  subscription: 'funnel_paywall_subscription_viewed',
  chat_no_match: 'funnel_paywall_chat_no_match_viewed',
  likes_you: 'funnel_paywall_likes_you_viewed',
  like_limit_reached: 'funnel_paywall_like_limit_reached_viewed',
  super_like_no_access: 'funnel_paywall_super_like_no_access_viewed',
  super_like_limit_reached: 'funnel_paywall_super_like_limit_reached_viewed',
  photo_in_chat: 'funnel_paywall_photo_in_chat_viewed',
  view_photo_in_chat: 'funnel_paywall_view_photo_in_chat_viewed',
  compatibility_details: 'funnel_paywall_compatibility_details_viewed'
}

const BENEFIT_KEYS_FEMALE = [
  { icon: '✨', titleKey: 'paywall.benefitCompatibilityTitle', descKey: 'paywall.benefitCompatibilityDesc' }
]

const Paywall = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { user, profile } = useAuth()
  const { trackEvent } = useAnalytics()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [legalModal, setLegalModal] = useState(null)
  const [consentWithdrawal, setConsentWithdrawal] = useState(true)
  const [consentTerms, setConsentTerms] = useState(true)
  const [consentError, setConsentError] = useState(false)
  const consentRef = useRef(null)

  const [searchParams] = useSearchParams()
  const reason = searchParams.get('reason') || ''

  const hasSubscription = profile?.subscription_status === 'active' || profile?.subscription_status === 'trial'
  useEffect(() => {
    if (profile && hasSubscription) {
      navigate('/app', { replace: true })
    }
  }, [profile, hasSubscription, navigate])

  useEffect(() => {
    if (profile && hasSubscription) return
    const paywallReason = reason || 'subscription'
    // Эти funnel-ивенты шлём в ChatDetail в момент клика (там же условия !isFemale и т.д.), иначе часть сценариев в PostHog не попадала
    if (paywallReason === 'photo_in_chat' || paywallReason === 'view_photo_in_chat') return
    const eventName = REASON_TO_PAYWALL_EVENT[paywallReason] || 'funnel_paywall_subscription_viewed'
    trackEvent(eventName, { reason: paywallReason })
  }, [reason, trackEvent, profile, hasSubscription])

  const isFemale = profile?.gender === 'female'
  const title = isFemale ? t('paywall.titleFemale') : t('paywall.titleMale')
  const badge = isFemale ? '🌙' : '✨'

  const benefits = useMemo(() => {
    const build = (list) => list.map((b) => ({
      key: b.key,
      icon: b.icon,
      title: t(b.titleKey),
      description: t(b.descKey)
    }))
    if (isFemale) return build(BENEFIT_KEYS_FEMALE)
    const highlightKey = REASON_TO_BENEFIT_KEY[reason]
    if (!highlightKey) return build(BENEFIT_KEYS_MALE)
    const idx = BENEFIT_KEYS_MALE.findIndex(b => b.key === highlightKey)
    if (idx <= 0) return build(BENEFIT_KEYS_MALE)
    const reordered = [...BENEFIT_KEYS_MALE]
    const [item] = reordered.splice(idx, 1)
    reordered.unshift(item)
    return build(reordered)
  }, [isFemale, reason, t])

  const highlightedKey = isFemale ? null : (REASON_TO_BENEFIT_KEY[reason] || null)

  const handleClose = () => {
    const from = location.state?.from || '/app'
    navigate(from, { replace: true })
  }

  const handleStartTrial = async () => {
    if (!user) return
    if (!consentWithdrawal || !consentTerms) {
      setConsentError(true)
      consentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    setError('')
    setConsentError(false)
    setLoading(true)
    const paywallReason = reason || 'subscription'
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      const url = NETLIFY_FUNCTIONS ? `${NETLIFY_FUNCTIONS}/create-checkout-session` : '/.netlify/functions/create-checkout-session'
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ reason: paywallReason })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(data.error || t('paywall.checkoutError'))
        setLoading(false)
        return
      }
      if (data.url) {
        window.location.href = data.url
        return
      }
      setError(t('paywall.noCheckoutUrl'))
    } catch (e) {
      console.error(e)
      setError(t('paywall.checkoutFailed'))
    }
    setLoading(false)
  }

  return (
    <div className="paywall-screen">
      <button type="button" className="paywall-close" onClick={handleClose} aria-label={t('common.close')}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div className="paywall-content">
        <div className="paywall-hero">
          <span className="paywall-badge" aria-hidden="true">{badge}</span>
          <div className="paywall-image">
            <img src="/Paywall.png" alt="" />
          </div>
        </div>

        <h1 className="paywall-title">{title}</h1>

        <div className="paywall-benefits">
          {benefits.map((benefit, index) => (
            <div
              key={benefit.key || index}
              className={`benefit-item${benefit.key && highlightedKey === benefit.key ? ' benefit-item--highlight' : ''}`}
            >
              <div className="benefit-icon">{benefit.icon}</div>
              <div className="benefit-content">
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="paywall-footer-text">
          {t('paywall.footer')}
        </p>

        <div className="paywall-consent" ref={consentRef}>
          <label className={`paywall-checkbox-label${consentError && !consentWithdrawal ? ' paywall-checkbox-label--error' : ''}`}>
            <input
              type="checkbox"
              checked={consentWithdrawal}
              onChange={(e) => { setConsentWithdrawal(e.target.checked); setConsentError(false) }}
              className="paywall-checkbox"
            />
            <span className="paywall-checkbox-text">{t('paywall.consentWithdrawal')}</span>
          </label>
          <label className={`paywall-checkbox-label${consentError && !consentTerms ? ' paywall-checkbox-label--error' : ''}`}>
            <input
              type="checkbox"
              checked={consentTerms}
              onChange={(e) => { setConsentTerms(e.target.checked); setConsentError(false) }}
              className="paywall-checkbox"
            />
            <span className="paywall-checkbox-text">
              {t('paywall.consentTermsBefore')}
              <button type="button" className="paywall-inline-link" onClick={() => setLegalModal('terms-and-conditions')}>
                {t('paywall.consentTermsLink')}
              </button>
              {t('paywall.consentTermsAfter')}
            </span>
          </label>
          <p className="paywall-data-controller">
            {t('paywall.dataControllerBefore')}
            <button type="button" className="paywall-inline-link" onClick={() => setLegalModal('privacy-policy')}>
              {t('paywall.dataControllerPrivacyLink')}
            </button>
            {t('paywall.dataControllerAfter')}
          </p>
        </div>
      </div>

      <div className="paywall-cta-wrap">
        {error && <p className="paywall-error">{error}</p>}
        <button
          type="button"
          className="cta-button paywall-cta"
          onClick={handleStartTrial}
          disabled={loading}
        >
          <span className="cta-main-text">{loading ? <Loader text={t('common.loading')} inline /> : t('paywall.cta')}</span>
        </button>
      </div>

      {legalModal && (
        <Modal
          title={legalModal === 'terms-and-conditions' ? t('paywall.consentTermsLink') : t('paywall.dataControllerPrivacyLink')}
          onClose={() => setLegalModal(null)}
        >
          {legalModal === 'terms-and-conditions' ? (
            <div style={{ maxHeight: '70vh', overflow: 'hidden', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)' }}>
              <iframe
                src={TERMS_PDF_URL}
                title={t('paywall.consentTermsLink')}
                style={{ width: '100%', height: '70vh', border: 'none' }}
              />
              <p className="p" style={{ marginTop: 12 }}>
                If the document doesn&apos;t load,{' '}
                <a href={TERMS_PDF_URL} target="_blank" rel="noreferrer">
                  open it in a new tab
                </a>
                .
              </p>
            </div>
          ) : (
            <LegalDocContent docId={legalModal} />
          )}
        </Modal>
      )}
    </div>
  )
}

export default Paywall
